// usePwaInstall — permite instalar la PWA "a demanda" (no solo el banner
// automático que el navegador muestra una vez y luego esconde).
//
// Cómo funciona:
//  - Chromium (Chrome/Edge/Android) dispara `beforeinstallprompt` cuando la app
//    es instalable. Lo capturamos al arrancar (initPwaInstall en main.ts) y lo
//    guardamos para poder lanzar el diálogo nativo cuando el usuario quiera.
//  - iOS/Safari NO tiene API de instalación programática: ahí el botón abre una
//    guía con los pasos manuales (Compartir → Agregar a inicio).
//  - `appinstalled` y display-mode standalone nos dicen si ya está instalada
//    para ocultar el botón.
import { readonly, ref } from 'vue';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let initialized = false;

// Estado compartido entre todos los consumidores (singleton de módulo).
const canInstall = ref(false);
const installed = ref(false);

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const mql = window.matchMedia?.('(display-mode: standalone)').matches === true;
  const iosStandalone = (navigator as unknown as { standalone?: boolean }).standalone === true;
  return mql || iosStandalone;
}

/** Detecta iPhone/iPad (incluye iPadOS 13+, que se reporta como Mac con touch). */
export function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  const touchPoints = (navigator as unknown as { maxTouchPoints?: number }).maxTouchPoints ?? 0;
  const iPadOS = navigator.platform === 'MacIntel' && touchPoints > 1;
  return iOSDevice || iPadOS;
}

/**
 * Registrar los listeners globales lo antes posible (main.ts), porque el
 * navegador puede disparar `beforeinstallprompt` antes de que monte el sidebar.
 * Idempotente.
 */
export function initPwaInstall(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  installed.value = isStandalone();
  window.addEventListener('beforeinstallprompt', (e) => {
    // Evitar el mini-infobar automático: mostramos nuestro propio botón.
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    canInstall.value = true;
  });
  window.addEventListener('appinstalled', () => {
    installed.value = true;
    canInstall.value = false;
    deferred = null;
  });
}

export type InstallOutcome = 'accepted' | 'dismissed' | 'unavailable' | 'ios';

export function usePwaInstall() {
  const iOS = isIOS();
  const showIOSHelp = ref(false);

  async function promptInstall(): Promise<InstallOutcome> {
    if (iOS) {
      // Safari no permite instalar por código: abrimos la guía manual.
      showIOSHelp.value = true;
      return 'ios';
    }
    if (!deferred) return 'unavailable';
    await deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === 'accepted') canInstall.value = false;
    deferred = null;
    return choice.outcome;
  }

  return {
    canInstall: readonly(canInstall),
    installed: readonly(installed),
    isIOS: iOS,
    showIOSHelp,
    promptInstall,
  };
}
